import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColDef, RowClickedEvent } from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { PlusIcon } from 'lucide-react';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { SettingsListDetailLayout } from '@/components/common/SettingsListDetailLayout';
import { Button } from '@/components/ui/button';
import { useGridDeselectOnClose } from '@/hooks/useGridDeselectOnClose';
import type { CostGroup } from '../services/costGroupService';
import { costGroupService } from '../services/costGroupService';
import {
  agGridCSSClasses,
  agGridDefaultColDef,
  agGridDefaultOptions,
  agGridSettingsContainerStyles,
  agGridTheme,
  agGridThemeStyles,
} from '../theme/agGridTheme';
import { CostGroupDrawer } from './CostGroupDrawer.tsx';

type DetailPanelMode = 'empty' | 'selected' | 'edit' | 'add';

export const CostGroupsSection: React.FC = () => {
  const queryClient = useQueryClient();
  const justSavedRef = useRef(false);
  const gridRef = useRef<AgGridReact>(null);
  const [panelMode, setPanelMode] = useState<DetailPanelMode>('empty');
  const [selectedGroup, setSelectedGroup] = useState<CostGroup | null>(null);
  const [editingGroup, setEditingGroup] = useState<{ id: string; name: string } | null>(null);

  const { data: costGroups = [], isLoading } = useQuery({
    queryKey: ['cost-groups'],
    queryFn: () => costGroupService.getCostGroups(),
  });

  const createMutation = useMutation({
    mutationFn: (name: string) => costGroupService.createCostGroup({ name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-groups'] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      costGroupService.updateCostGroup(id, { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-groups'] });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => costGroupService.deleteCostGroup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cost-groups'] });
      queryClient.invalidateQueries({ queryKey: ['cost-group-year-rates'] });
      queryClient.invalidateQueries({ queryKey: ['cost-group-assignments'] });
    },
  });

  const handleRowClicked = useCallback((event: RowClickedEvent<CostGroup>) => {
    if (!event.data) return;
    setSelectedGroup(event.data);
    setPanelMode('selected');
    setEditingGroup(null);
  }, []);

  const handleFormClose = () => {
    if (justSavedRef.current) {
      justSavedRef.current = false;
      setEditingGroup(null);
      return;
    }
    setPanelMode((mode) => (mode === 'edit' ? 'selected' : 'empty'));
    setEditingGroup(null);
  };

  const handleViewClose = () => {
    setPanelMode('empty');
    setSelectedGroup(null);
    setEditingGroup(null);
  };

  const handleDelete = (group: CostGroup) => {
    if (window.confirm('Delete this group?')) {
      deleteMutation.mutate(group.id);
      setSelectedGroup(null);
      setPanelMode('empty');
    }
  };

  const columnDefs: ColDef<CostGroup>[] = useMemo(
    () => [
      {
        headerName: 'Group name',
        field: 'name',
        width: 180,
        filter: 'agSetColumnFilter',
        filterParams: {
          buttons: ['reset', 'apply'],
        },
        headerClass: agGridCSSClasses.modernHeader,
        cellClass: agGridCSSClasses.modernCell,
      },
    ],
    []
  );

  const gridOptions = useMemo(
    () => ({
      ...agGridDefaultOptions,
      theme: agGridTheme,
      onRowClicked: handleRowClicked,
      rowSelection: { mode: 'singleRow' as const, checkboxes: false },
    }),
    [handleRowClicked]
  );

  const detailOpen = panelMode !== 'empty';

  useGridDeselectOnClose(gridRef, detailOpen);

  return (
    <SettingsListDetailLayout
      detailOpen={detailOpen}
      detail={
        panelMode === 'edit' || panelMode === 'add' ? (
          <CostGroupDrawer
            isOpen={true}
            onClose={handleFormClose}
            group={editingGroup}
            onSave={async (name, id) => {
              if (id) await updateMutation.mutateAsync({ id, name });
              else await createMutation.mutateAsync(name);
              justSavedRef.current = true;
              setPanelMode('empty');
              setSelectedGroup(null);
            }}
          />
        ) : panelMode === 'selected' && selectedGroup ? (
          <CostGroupDrawer
            key={selectedGroup.id}
            isOpen={true}
            readOnly
            group={selectedGroup}
            onClose={handleViewClose}
            onSave={() => undefined}
            onEditRequest={() => {
              setEditingGroup({ id: selectedGroup.id, name: selectedGroup.name });
              setPanelMode('edit');
            }}
            onDeleteRequest={() => handleDelete(selectedGroup)}
          />
        ) : null
      }
      list={
        <>
          <style>{agGridThemeStyles}</style>
          <div className="mb-4 flex shrink-0 items-center justify-end">
            <Button
              type="button"
              onClick={() => {
                setEditingGroup(null);
                setPanelMode('add');
              }}
            >
              <PlusIcon data-icon="inline-start" />
              Add group
            </Button>
          </div>
          {isLoading ? (
            <p className="text-text-secondary">Loading...</p>
          ) : (
            <div className="ag-theme-material min-h-0 flex-1" style={agGridSettingsContainerStyles}>
              <AgGridReact
                ref={gridRef}
                rowData={costGroups}
                columnDefs={columnDefs}
                defaultColDef={agGridDefaultColDef}
                gridOptions={gridOptions}
                animateRows
                domLayout="normal"
                rowNumbers={true}
                getRowId={(params) => params.data.id}
              />
            </div>
          )}
        </>
      }
    />
  );
};
